import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { MarginCalculatorMarginService } from '../../services/margin-calculator-margin.service';
import {
    ContractMinDaysStatusActions,
    MarginCalculatorApiActions,
    MarginCalculatorComputationActions,
    MarginCalculatorPageActions
} from '../actions';
import {
    selectBaseBillingRate,
    selectClinicianId,
    selectEndDate,
    selectFacilityId,
    selectStartDate
} from '../selectors/margin-calculator.selectors';
import dayjs from 'dayjs';

@Injectable()
export class ContractMinDaysEffects {
    checkforMinDaysStatusUpdate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorPageActions.setClinician,
                MarginCalculatorApiActions.clinicianLoadedSuccess,
                MarginCalculatorPageActions.setFacility,
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorPageActions.setStartDate,
                MarginCalculatorApiActions.getContractDatesLoadedSuccess,
                MarginCalculatorApiActions.getContractEndDateLoadedSuccess,
                MarginCalculatorPageActions.setEndDate,
                MarginCalculatorPageActions.setBaseBillingRate,
                MarginCalculatorPageActions.baseBillingRateChanged,
                MarginCalculatorComputationActions.baseBillingRateComputedSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(selectFacilityId),
                this.store.select(selectClinicianId),
                this.store.select(selectBaseBillingRate),
                this.store.select(selectStartDate),
                this.store.select(selectEndDate)
            ]),
            switchMap(([, facilityId, clinicianId, baseBillingRate, startDate, endDate]) => {
                if (!!clinicianId && !!facilityId && !!startDate && !!endDate) {
                    return of(
                        ContractMinDaysStatusActions.loadStatus({
                            candidateId: clinicianId,
                            facilityId,
                            billingRate: baseBillingRate?.value ?? 0,
                            startDate: dayjs(startDate).toDate(),
                            endDate: dayjs(endDate).toDate()
                        })
                    );
                }
                return of(MarginCalculatorPageActions.noOp({ debug: 'checkforMinDaysStatusUpdate$ effect' }));
            })
        );
    });

    loadContractMinDaysStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ContractMinDaysStatusActions.loadStatus),
            concatMap(({ candidateId, facilityId, billingRate, startDate, endDate }) => {
                return this._marginService
                    .getContractMinDaysStatus(candidateId, facilityId, billingRate, startDate, endDate)
                    .pipe(
                        map((status) => ContractMinDaysStatusActions.loadStatusSuccess({ status })),
                        catchError(() =>
                            of(
                                ContractMinDaysStatusActions.loadStatusFail({
                                    message: 'Failed to load contract min dates status'
                                })
                            )
                        )
                    );
            })
        );
    });

    addContractMinDaysStatusToCache$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ContractMinDaysStatusActions.addStatusToCache),
            switchMap((action) => of(ContractMinDaysStatusActions.loadStatusSuccess({ status: action.status })))
        );
    });

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly _marginService: MarginCalculatorMarginService
    ) {}
}
